import {
  ShortAlertResponse,
  ShortAlertsCachedResponse,
} from 'app/modules/alerts/types/responses';

import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';

import { post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { PaginationPayload } from 'app/shared/pagination/models';
import {
  selectHasReadAlertsPermission,
  selectHasReadEntitiesPermissions,
} from 'app/modules/session/selectors';
import { useSelector } from 'react-redux';
import { selectDLEnabledForEntityDetails } from 'app/shared/featureFlags/selectors';
import { GetAlertsPayload } from 'app/modules/alerts/requests';

interface LegacyEntityAlertsResponse {
  count: number;
  alerts: ShortAlertResponse[];
  data_loader: undefined;
}

type AlertsResponse = ShortAlertsCachedResponse | LegacyEntityAlertsResponse;

export const useEntityAlerts = ({
  entityExternalId,
  payload,
}: {
  entityExternalId: string;
  payload: PaginationPayload;
}) => {
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const hasReadEntityPermission = useSelector(selectHasReadEntitiesPermissions);
  const useCacheEndpoint = useSelector(selectDLEnabledForEntityDetails);

  const hasValidEntityId = entityExternalId.length > 0;

  return useQuery<AlertsResponse>({
    queryFn: () => {
      if (useCacheEndpoint) {
        return post<ShortAlertsCachedResponse>(`/v1/alerts/cached/list`, {
          ...payload,
          entity_external_ids: [entityExternalId],
        } satisfies GetAlertsPayload);
      }
      return post<LegacyEntityAlertsResponse>(
        `/v1/entities/${encodeURIComponent(entityExternalId)}/paginated-alerts`,
        payload,
      );
    },
    queryKey: ENTITY_QUERY_KEYS.getEntityAlerts(
      entityExternalId,
      payload,
      useCacheEndpoint,
    ),
    enabled:
      hasValidEntityId && hasReadAlertsPermission && hasReadEntityPermission,
  });
};
